import React from 'react';
import { Box, Container, Typography, Chip, Divider } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

const sections = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    content: [
      {
        subtitle: 'Account Information',
        text: 'When you create a BaaStream account, we collect your name, email address, and password (stored as a secure hash). For paid plans, we collect billing information processed securely through our payment processors (Stripe or Razorpay). Monks Do Work does not store raw card numbers or payment instrument details.',
      },
      {
        subtitle: 'Usage Data',
        text: 'We collect information about how you use BaaStream, including scan counts, rule execution logs (metadata only — not the content of your files), CLI version, operating system, and feature usage. This data is used to improve the Service, enforce plan limits, and analyse product usage trends.',
      },
      {
        subtitle: 'Scan Result Metadata',
        text: 'When you run a scan, the CLI transmits scan result metadata (pass/fail counts, rule IDs, timestamps) to the BaaStream dashboard over TLS. Monks Do Work does not receive, store, or process the underlying configuration files, FHIR resources, log files, or any Protected Health Information (PHI) you scan. PHI stays entirely within your environment.',
      },
      {
        subtitle: 'Communications',
        text: 'If you contact us by email or through our support channels, we retain those communications to resolve your inquiry and improve our Service.',
      },
      {
        subtitle: 'Device & Log Data',
        text: 'Like most web services, we automatically collect limited technical information when you access the dashboard: IP address, browser type, referring URL, and pages visited. This data is used for security monitoring and service improvement.',
      },
    ],
  },
  {
    id: 'how-we-use-information',
    title: '2. How We Use Your Information',
    content: [
      {
        subtitle: null,
        text: 'Monks Do Work uses the information we collect to:',
      },
      {
        subtitle: null,
        text: '• Provide, maintain, and improve the BaaStream service\n• Authenticate your identity and manage your account\n• Process payments and send billing communications\n• Send transactional emails (scan summaries, account alerts, plan updates)\n• Respond to support requests and service inquiries\n• Enforce our Terms and Conditions and plan limits\n• Detect, investigate, and prevent fraud, abuse, and security incidents\n• Comply with applicable legal and regulatory obligations\n• Conduct anonymised, aggregated analytics to improve the product',
      },
      {
        subtitle: null,
        text: 'We do not sell, rent, or trade your personal information to third parties for their marketing purposes.',
      },
    ],
  },
  {
    id: 'data-security',
    title: '3. Data Security',
    content: [
      {
        subtitle: null,
        text: 'Monks Do Work implements industry-standard technical and organisational security measures to protect your data, including:',
      },
      {
        subtitle: null,
        text: '• TLS 1.2+ encryption for all data in transit\n• Encryption at rest for stored account and scan metadata\n• Role-based, least-privilege access controls for internal systems\n• Regular security reviews and vulnerability assessments\n• Bcrypt password hashing — plaintext passwords are never stored\n• Audit logging of administrative and privileged actions\n• Isolated, access-controlled cloud infrastructure (Google Firebase / GCP)',
      },
      {
        subtitle: null,
        text: 'No method of transmission over the Internet or electronic storage is 100% secure. While we apply commercially reasonable efforts to protect your data, we cannot guarantee absolute security. In the event of a data breach that affects your rights and freedoms, we will notify you in accordance with applicable law.',
      },
    ],
  },
  {
    id: 'phi-and-hipaa',
    title: '4. PHI and HIPAA',
    content: [
      {
        subtitle: null,
        text: 'BaaStream is architecturally designed so that Protected Health Information (PHI) never leaves your environment. The CLI scanner executes locally within your own infrastructure. Only scan result metadata — rule IDs, pass/fail status, counts, and timestamps — is transmitted to the BaaStream dashboard operated by Monks Do Work.',
      },
      {
        subtitle: null,
        text: 'Monks Do Work is not a Covered Entity or Business Associate under HIPAA with respect to PHI you process locally using the CLI. Enterprise customers who require a Business Associate Agreement (BAA) for any dashboard data they consider PHI may contact us at privacy@baastream.com to discuss BAA execution.',
      },
    ],
  },
  {
    id: 'third-party-services',
    title: '5. Third-Party Services',
    content: [
      {
        subtitle: null,
        text: 'To operate BaaStream, Monks Do Work uses the following third-party service providers. Each operates under its own privacy policy and is bound by contractual data protection obligations with us:',
      },
      {
        subtitle: null,
        text: '• Payment processing: Stripe (international), Razorpay (India and applicable regions)\n• Cloud infrastructure and authentication: Google Firebase / Google Cloud Platform (GCP)\n• Transactional email: Email delivery provider for account and billing notifications\n• Analytics: Internal, first-party analytics only — we do not integrate third-party advertising or cross-site tracking scripts',
      },
      {
        subtitle: null,
        text: 'We share only the minimum data necessary for each provider to fulfil their function. We do not authorise any provider to use your data for their own marketing or advertising purposes.',
      },
    ],
  },
  {
    id: 'data-retention',
    title: '6. Data Retention',
    content: [
      {
        subtitle: null,
        text: 'We retain personal account data for as long as your account remains active. Scan result metadata is retained for the period covered by your active plan (rolling 90 days for Community; 12 months for paid plans). After a plan downgrade, older scan data outside the new retention window is deleted within 30 days.',
      },
      {
        subtitle: null,
        text: 'Upon account deletion, Monks Do Work will delete or anonymise your personal data within 30 days, except where retention is required by applicable law (e.g., financial records, tax obligations) or where data has been anonymised and cannot reasonably identify you.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: '7. Your Rights',
    content: [
      {
        subtitle: null,
        text: 'Depending on your jurisdiction, you may have rights under applicable data protection law, including:',
      },
      {
        subtitle: null,
        text: '• Right of access — obtain a copy of the personal data we hold about you\n• Right to rectification — correct inaccurate or incomplete data\n• Right to erasure — request deletion of your personal data ("right to be forgotten")\n• Right to restriction — request that we limit how we process your data\n• Right to data portability — receive your data in a structured, machine-readable format\n• Right to object — object to processing based on legitimate interests\n• Right to withdraw consent — where processing is based on consent, withdraw it at any time without affecting prior lawful processing',
      },
      {
        subtitle: null,
        text: 'To exercise any of these rights, submit a request to privacy@baastream.com. We will respond within 30 days. We may ask you to verify your identity before acting on the request.',
      },
    ],
  },
  {
    id: 'cookies',
    title: '8. Cookies',
    content: [
      {
        subtitle: null,
        text: 'The BaaStream dashboard uses strictly necessary cookies (session authentication, CSRF protection) and functional cookies (user preferences such as theme and billing toggle). We do not use advertising cookies, third-party tracking pixels, or cross-site behavioural targeting. You can manage or clear cookies through your browser settings; disabling strictly necessary cookies will prevent you from logging in.',
      },
    ],
  },
  {
    id: 'international-transfers',
    title: '9. International Data Transfers',
    content: [
      {
        subtitle: null,
        text: 'Monks Do Work is headquartered in India. Your data may be processed on servers located in India or in other countries where our cloud infrastructure providers (Google Cloud Platform) operate data centres, including the United States and the European Union. Where data is transferred outside your country of residence, we ensure appropriate safeguards are in place in accordance with applicable data protection law.',
      },
    ],
  },
  {
    id: 'childrens-privacy',
    title: '10. Children\'s Privacy',
    content: [
      {
        subtitle: null,
        text: 'BaaStream is a professional B2B service intended for persons aged 18 and over. We do not knowingly collect personal data from children under 18. If you believe a minor has provided us with personal data, please contact us at privacy@baastream.com and we will delete it promptly.',
      },
    ],
  },
  {
    id: 'changes',
    title: '11. Changes to This Policy',
    content: [
      {
        subtitle: null,
        text: 'Monks Do Work may update this Privacy Policy from time to time to reflect changes in our practices, technology, or applicable law. We will notify you of material changes by email (to the address associated with your account) or by posting a prominent notice on the BaaStream dashboard at least 14 days before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the updated Policy.',
      },
    ],
  },
  {
    id: 'contact',
    title: '12. Contact Us',
    content: [
      {
        subtitle: null,
        text: 'For privacy-related questions, data subject requests, or to contact our privacy team:\n\nMonks Do Work (operating as BaaStream)\nPrivacy Team: privacy@baastream.com\n\nFor general support: support@baastream.com\nFor billing enquiries: billing@baastream.com',
      },
    ],
  },
];

export function PrivacyPolicy() {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal Nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box
            component="a"
            href="/"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}
          >
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
              BaaStream
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, pb: { xs: 6, md: 8 }, pt: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Chip
            label="Legal"
            size="small"
            sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }}
          />
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 2 }}
          >
            Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Last updated: April 27, 2025
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.9, fontSize: '1.05rem', mb: 5 }}>
          BaaStream is a product of <strong>Monks Do Work</strong> ("Company", "we", "our", or "us").
          This Privacy Policy explains how Monks Do Work collects, uses, stores, discloses, and
          protects information when you use the BaaStream compliance scanning service, including our
          website, dashboard, CLI tool, and APIs (collectively, the "Service").
        </Typography>

        {/* Table of Contents */}
        <Box sx={{ bgcolor: '#fafafa', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 2, p: 3, mb: 6 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: DARK, mb: 2 }}>
            Contents
          </Typography>
          {sections.map((s) => (
            <Box key={s.id} sx={{ mb: 0.5 }}>
              <Box
                component="a"
                href={`#${s.id}`}
                sx={{ color: PRIMARY, textDecoration: 'none', fontSize: '0.9rem', '&:hover': { textDecoration: 'underline' } }}
              >
                {s.title}
              </Box>
            </Box>
          ))}
        </Box>

        {sections.map((section, i) => (
          <Box key={section.id} id={section.id} sx={{ mb: 6 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.3rem', md: '1.5rem' }, mb: 3 }}>
              {section.title}
            </Typography>
            {section.content.map((block, j) => (
              <Box key={j} sx={{ mb: 2 }}>
                {block.subtitle && (
                  <Typography variant="body1" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>
                    {block.subtitle}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{ color: '#555', lineHeight: 1.9, whiteSpace: 'pre-line' }}
                >
                  {block.text}
                </Typography>
              </Box>
            ))}
            {i < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
          </Box>
        ))}
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2025 Monks Do Work. All rights reserved. BaaStream is a product of Monks Do Work.
            </Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              ← Back to Home
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
